import { useAuthModalStore } from '@/stores/authModalStore'
import { useAuthStore } from '@/stores/authStore'
import { useFeatureFlagsStore } from '@/stores/featureFlagsStore'
import { useThemeStore } from '@/stores/themeStore'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { UserDropdown } from './UserDropdown'

const MENU = [
  { key: 'datasets', label: 'Datasets', href: '/datasets', prefix: '/datasets' },
  { key: 'curations', label: 'Curations', href: '/curations', prefix: '/curations' },
  { key: 'models', label: 'Models', href: '/models', prefix: '/models' },
  { key: 'mlops', label: 'MLOps', href: '/mlops/trainings', prefix: '/mlops' },
  { key: 'leaderboard', label: 'Leaderboard', href: '/leaderboard', prefix: '/leaderboard' },
] as const

/**
 * Navbar — A안 GNB 정합 + 모바일 햄버거.
 *
 * 데스크탑 (lg+): 메뉴 5 + 테마 + Sign In (full).
 * 모바일 (<lg): 로고 + Sign In + 햄버거 ☰ — 메뉴 5/테마는 드로어.
 */
export function Navbar() {
  const location = useLocation()
  const openAuthModal = useAuthModalStore((s) => s.open)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const mlopsEnabled = useFeatureFlagsStore((s) => s.mlopsEnabled)
  const siteName = useFeatureFlagsStore((s) => s.siteName)
  const theme = useThemeStore((s) => s.theme)
  const toggleTheme = useThemeStore((s) => s.toggle)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  const isActive = (prefix: string): boolean =>
    location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
  const visibleMenu = MENU.filter((m) => m.key !== 'mlops' || mlopsEnabled)

  const handleSignIn = (): void => {
    setMobileOpen(false)
    openAuthModal('signin')
  }

  return (
    <nav className="gnb relative" aria-label="주 메뉴">
      <Link
        to="/"
        className="flex shrink-0 items-center gap-2 text-[15px] font-bold text-nav-text no-underline"
        aria-label={siteName}
      >
        <span
          aria-hidden="true"
          className="grid h-7 w-7 place-items-center rounded-md text-[15px] font-black text-white"
          style={{ background: '#ff6b35' }}
        >
          K
        </span>
        {/* 이름은 관리자 설정(사이트 이름)을 따른다 — 2026-10-20 이슈 #8 "K-Humanoid Hub" */}
        <span>{siteName}</span>
      </Link>

      {/* 데스크탑 메뉴 — lg+ */}
      <div className="gnb-menu hidden lg:flex">
        {visibleMenu.map((m) => {
          const active = isActive(m.prefix)
          return (
            <Link
              key={m.key}
              to={m.href}
              className={active ? 'active' : undefined}
              aria-current={active ? 'page' : undefined}
            >
              {m.label}
            </Link>
          )
        })}
      </div>

      {/* spacer — 모바일에서 brand 와 우측 그룹 사이 */}
      <div className="flex-1 lg:hidden" />

      {/* 데스크탑 테마 — lg+ */}
      <button
        type="button"
        className="hidden shrink-0 rounded px-3 py-2 text-[12px] text-nav-text hover:bg-white/10 lg:inline-flex"
        aria-label={theme === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
        onClick={toggleTheme}
      >
        {theme === 'dark' ? '☀' : '☾'}
      </button>
      {/* Sign In / UserDropdown — 모든 viewport */}
      <div className="shrink-0">
        {isAuthenticated ? (
          <UserDropdown />
        ) : (
          <button type="button" className="signin-btn" onClick={handleSignIn}>
            Sign In
          </button>
        )}
      </div>

      {/* 모바일 햄버거 토글 — <lg */}
      <button
        type="button"
        className="grid h-9 w-9 shrink-0 place-items-center rounded text-[18px] text-nav-text hover:bg-white/10 lg:hidden"
        aria-label={mobileOpen ? '메뉴 닫기' : '메뉴 열기'}
        aria-expanded={mobileOpen}
        aria-controls="mobile-menu"
        onClick={() => setMobileOpen((v) => !v)}
      >
        {mobileOpen ? '✕' : '☰'}
      </button>

      {/* 모바일 드로어 — Navbar 바로 아래, 항목 우측 정렬 */}
      {mobileOpen && (
        <div
          id="mobile-menu"
          className="absolute left-0 right-0 top-full z-50 border-t border-white/10 bg-nav-bg shadow-lg lg:hidden"
        >
          <div className="flex flex-col items-end gap-1 px-4 py-3">
            {visibleMenu.map((m) => {
              const active = isActive(m.prefix)
              return (
                <Link
                  key={m.key}
                  to={m.href}
                  className={`rounded px-3 py-2.5 text-right text-[14px] no-underline ${
                    active
                      ? 'bg-white/10 font-semibold text-nav-text'
                      : 'text-nav-text-dim hover:bg-white/5 hover:text-nav-text'
                  }`}
                  aria-current={active ? 'page' : undefined}
                >
                  {m.label}
                </Link>
              )
            })}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-white/10 px-4 py-3">
            <button
              type="button"
              className="rounded px-3 py-1.5 text-[12px] text-nav-text-dim hover:bg-white/10 hover:text-nav-text"
              aria-label={theme === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
              onClick={toggleTheme}
            >
              {theme === 'dark' ? '☀ Light' : '☾ Dark'}
            </button>
          </div>
        </div>
      )}
    </nav>
  )
}
